import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./LoginGoogle.css"; // Import the CSS file for styling
import logo from "./assets/images/aiprice-logo.png"; // Import the logo image
import { FaGoogle } from "react-icons/fa"; // Import Google icon
import { getDatabase, ref, set, get } from "firebase/database";
import { app } from "./firebase/config"; // Import app từ config
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import {
  handleAffiliateLink, // Import hàm xử lý liên kết tiếp thị liên kết
  trackAffiliateSignup, // Import hàm theo dõi đăng ký qua tiếp thị liên kết
} from "./utils/affiliateUtils";
import { trackEvent, trackCompleteRegistration } from "./utils/metaPixel";
import { captureFacebookAdParams, getStoredAdParams } from "./utils/adTracking";
import TermsAcceptanceModal from "./components/TermsAcceptanceModal";

const auth = getAuth(app);
const provider = new GoogleAuthProvider();

// Hàm lưu thông tin người dùng vào database
const saveUserToDatabase = async (user) => {
  try {
    console.log("🔄 Bắt đầu lưu thông tin người dùng vào database...");
    const db = getDatabase();
    const userRef = ref(db, `users/${user.uid}`);

    // Kiểm tra xem người dùng đã tồn tại chưa
    const snapshot = await get(userRef);

    if (!snapshot.exists()) {
      // Người dùng mới - tạo với các thông tin cơ bản
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      // Làm tròn lên đến giờ tiếp theo
      oneMonthFromNow.setMinutes(0);
      oneMonthFromNow.setSeconds(0);
      oneMonthFromNow.setMilliseconds(0);
      oneMonthFromNow.setHours(oneMonthFromNow.getHours() + 1);

      // Chuẩn bị dữ liệu người dùng
      const userData = {
        userID: user.uid,
        createdAt: Date.now(),
        dailyPostCount: {},
        profile: {
          displayName: user.displayName || null,
          email: user.email || null,
          photoURL: user.photoURL || null,
          authProvider: "google",
        },
      };

      // Lưu thông tin người dùng mới vào database
      await set(userRef, userData);
      console.log("✅ Đã lưu thông tin người dùng mới vào database:", user.uid);
      return { success: true, isNewUser: true, needsTermsAcceptance: true };
    } else {
      // Người dùng đã tồn tại - cập nhật thông tin profile nếu cần
      const userData = snapshot.val();
      const profile = userData.profile || {};

      // Chỉ cập nhật thông tin chưa có
      const updatedProfile = {
        ...profile,
        displayName: profile.displayName || user.displayName || null,
        email: profile.email || user.email || null,
        photoURL: profile.photoURL || user.photoURL || null,
        authProvider: profile.authProvider || "google",
      };

      // Cập nhật thông tin profile
      await set(ref(db, `users/${user.uid}/profile`), updatedProfile);
      console.log("✅ Đã cập nhật thông tin profile người dùng:", user.uid);

      // QUAN TRỌNG: Kiểm tra xem user đã đồng ý terms chưa
      const termsAcceptanceRef = ref(db, `users/${user.uid}/termsAcceptance`);
      const termsSnapshot = await get(termsAcceptanceRef);
      const hasAcceptedTerms = termsSnapshot.exists() && termsSnapshot.val()?.termsAccepted === true;

      console.log("📋 Kiểm tra terms acceptance:", {
        exists: termsSnapshot.exists(),
        data: termsSnapshot.val(),
        hasAcceptedTerms,
      });

      // Nếu chưa đồng ý terms, vẫn cần hiển thị modal
      return {
        success: true,
        isNewUser: false,
        needsTermsAcceptance: !hasAcceptedTerms,
      };
    }
  } catch (error) {
    console.error("❌ Lỗi khi lưu thông tin người dùng vào database:", error);
    // Trả về lỗi để có thể xử lý ở phía gọi hàm
    return { success: false, error };
  }
};

const LoginGoogle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Thêm flag để kiểm soát việc chuyển hướng tự động
  const [isManualLogin, setIsManualLogin] = useState(false);
  // Thêm flag để kiểm soát việc đang hiển thị popup Google
  const [isGooglePopupOpen, setIsGooglePopupOpen] = useState(false);
  // NEW: Terms acceptance modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [newUserData, setNewUserData] = useState(null);
  // Flag to prevent auto-navigation while checking new user status
  const [isCheckingNewUser, setIsCheckingNewUser] = useState(false);
  const [userID, setUserID] = useState(null);
  const [adParams, setAdParams] = useState(null);

  // Lấy returnUrl từ URL search params
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get("returnUrl") || "/ai-price";

  // Thêm class login-page vào body khi component mount và xóa khi unmount
  useEffect(() => {
    document.body.classList.add("login-page");
    return () => {
      document.body.classList.remove("login-page");
    };
  }, []);

  useEffect(() => {
    // Xử lý affiliate link khi component mount
    handleAffiliateLink(); // Xử lý các tham số tiếp thị liên kết trong URL

    // Capture ad parameters
    handleCaptureAdParams();

    // Xóa các trạng thái đăng nhập cũ ở localStorage
    if (!isManualLogin) {
      // Không xóa thông tin nếu đang trong quá trình đăng nhập thủ công
      localStorage.removeItem("userId");
      localStorage.removeItem("redirecting");
      localStorage.removeItem("returnUrl");
    }

    // Kiểm tra trạng thái đăng nhập
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsLoggedIn(true);
        setUserID(user.uid);
        // CHỈ lưu userID vào localStorage khi đăng nhập thủ công
        // và popup Google đã đóng
        if (isManualLogin && !isGooglePopupOpen) {
          localStorage.setItem("userId", user.uid);
        }
      } else {
        setIsLoggedIn(false);
        setUserID(null);
      }
    });

    return () => {
      // Cleanup khi component unmount
      unsubscribe();
      if (!isManualLogin) {
        localStorage.removeItem("redirecting");
      }
    };
  }, [location, isManualLogin, isGooglePopupOpen]);

  // Capture ad parameters from URL using our utility function
  const handleCaptureAdParams = () => {
    const params = captureFacebookAdParams();
    if (params) {
      setAdParams(params);
    }
  };

  // NEW: Handle terms acceptance
  const handleTermsAccept = async (acceptanceData) => {
    try {
      if (!newUserData) return;

      setIsLoading(true);
      setShowTermsModal(false);

      // Save acceptance to Firebase
      const db = getDatabase();
      const userRef = ref(db, `users/${newUserData.uid}/termsAcceptance`);
      await set(userRef, acceptanceData);

      console.log("✅ Đã lưu thông tin đồng ý điều khoản");

      // Continue with tracking affiliate and navigation
      trackAffiliateSignup(newUserData.uid)
        .then(() => {
          console.log("✅ Hoàn thành ghi nhận thông tin affiliate");
          setIsLoading(false);

          // IMPORTANT: Luôn navigate đến /ai-price sau khi accept terms
          // Clear returnUrl khỏi localStorage
          localStorage.removeItem("returnUrl");
          localStorage.removeItem("redirecting");

          console.log("🚀 Navigating to /ai-price after terms acceptance");
          navigate("/ai-price", { replace: true });
        })
        .catch((error) => {
          console.error("❌ Lỗi:", error);
          setIsLoading(false);
          // Still navigate even if tracking fails
          localStorage.removeItem("returnUrl");
          localStorage.removeItem("redirecting");

          console.log("🚀 Navigating to /ai-price after error");
          navigate("/ai-price", { replace: true });
        });
    } catch (error) {
      console.error("❌ Lỗi khi lưu đồng ý điều khoản:", error);
      setIsLoading(false);
    }
  };

  // NEW: Handle terms decline
  const handleTermsDecline = async () => {
    try {
      console.log("❌ User từ chối điều khoản");
      setShowTermsModal(false);
      setIsLoading(true);

      // QUAN TRỌNG: Đánh dấu trong database rằng user đã từ chối
      // (để tracking, có thể xóa user sau hoặc để họ thử lại)
      if (newUserData) {
        const db = getDatabase();
        const declineRef = ref(db, `users/${newUserData.uid}/termsAcceptance`);
        await set(declineRef, {
          termsAccepted: false,
          declinedAt: Date.now(),
        });
        console.log("📝 Đã ghi nhận việc từ chối điều khoản vào database");
      }

      // Log out user
      await auth.signOut();
      console.log("🔓 Đã đăng xuất user");

      // Clear localStorage
      localStorage.removeItem("userAccessToken");
      localStorage.removeItem("userAccessTokenExpiry");
      localStorage.removeItem("userId");
      localStorage.removeItem("returnUrl");
      localStorage.removeItem("redirecting");

      setIsLoggedIn(false);
      setUserID(null);
      setNewUserData(null);
      setIsLoading(false);
      setIsManualLogin(false);

      alert("Bạn cần đồng ý với điều khoản để sử dụng dịch vụ. Vui lòng đăng nhập lại khi bạn sẵn sàng đồng ý.");
    } catch (error) {
      console.error("❌ Lỗi khi từ chối điều khoản:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Chỉ chuyển hướng tự động khi đã đăng nhập thủ công
    // và popup Google đã đóng
    // QUAN TRỌNG: Không chuyển hướng nếu đang hiển thị modal terms hoặc đang kiểm tra user mới

    console.log("🔍 useEffect auto-navigation check:", {
      isLoggedIn,
      isManualLogin,
      isGooglePopupOpen,
      redirecting: localStorage.getItem("redirecting"),
      showTermsModal,
      isCheckingNewUser,
    });

    if (
      isLoggedIn &&
      isManualLogin &&
      !isGooglePopupOpen &&
      !localStorage.getItem("redirecting") &&
      !showTermsModal && // Ngăn chặn navigation khi modal đang hiển thị
      !isCheckingNewUser // Ngăn chặn navigation khi đang kiểm tra user mới
    ) {
      console.log("🚀 All conditions met - navigating automatically");
      // This useEffect will only redirect if we're not already in the process of redirecting
      const redirectUrl = localStorage.getItem("returnUrl") || "/ai-price";
      localStorage.removeItem("returnUrl");
      navigate(redirectUrl, { replace: true });
    } else {
      console.log("⏸️ Navigation blocked by one or more conditions");
    }
  }, [isLoggedIn, navigate, isManualLogin, isGooglePopupOpen, showTermsModal, isCheckingNewUser]);

  const handleGoogleLogin = () => {
    // Đánh dấu đây là đăng nhập thủ công
    setIsManualLogin(true);
    // Đánh dấu popup Google đang mở
    setIsGooglePopupOpen(true);

    // Lưu trữ returnUrl để sử dụng sau khi đăng nhập thành công
    localStorage.setItem("returnUrl", returnUrl);

    // Track the click event with Meta Pixel
    trackEvent("GoogleLoginClick", { button_location: "login_page" });

    setIsLoading(true);

    // Thêm giá trị prompt cho GoogleAuthProvider để luôn hiển thị popup chọn tài khoản
    provider.setCustomParameters({
      prompt: "select_account",
    });

    signInWithPopup(auth, provider)
      .then((result) => {
        // Đánh dấu popup Google đã đóng
        setIsGooglePopupOpen(false);

        // User signed in
        const user = result.user;
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;

        // Store auth token in localStorage
        localStorage.setItem("userAccessToken", token);

        // No expiry for Google tokens in the same way as FB tokens, but we'll set a default
        const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        localStorage.setItem("userAccessTokenExpiry", Date.now() + oneDay);

        // Get stored ad parameters using our utility function
        const adParams = getStoredAdParams() || {};

        // Track successful login with Meta Pixel with ad parameters
        trackCompleteRegistration({
          content_name: "google_login",
          status: "success",
          user_id: user.uid,
          ...adParams, // Include ad parameters in tracking
        });

        // IMPORTANT: Set flag to prevent auto-navigation while checking user status
        console.log("🔒 Set isCheckingNewUser = true");
        setIsCheckingNewUser(true);

        // Lưu thông tin profile và userID vào realtime database
        // và thực hiện các tác vụ khác sau khi lưu thành công
        saveUserToDatabase(user)
          .then((result) => {
            console.log("📊 saveUserToDatabase result:", result);

            // QUAN TRỌNG: Kiểm tra xem user có cần đồng ý terms không
            // (bất kể là user mới hay user cũ chưa đồng ý)
            if (result.needsTermsAcceptance) {
              if (result.isNewUser) {
                console.log("🆕 User mới phát hiện - chuẩn bị hiển thị modal terms");
              } else {
                console.log("⚠️ User đã tồn tại nhưng chưa đồng ý terms - hiển thị modal");
              }
              console.log("📝 Setting: newUserData, showTermsModal=true, isCheckingNewUser=false");

              setNewUserData(user);
              setShowTermsModal(true);
              setIsCheckingNewUser(false); // Done checking
              setIsLoading(false); // Stop loading while showing modal

              // Set login states AFTER setting modal states to prevent race condition
              console.log("✅ Setting login states");
              setIsLoggedIn(true);
              setUserID(user.uid);
              localStorage.setItem("userId", user.uid);

              console.log("⏸️ Rejecting promise chain to stop navigation");
              return Promise.reject({ needsTermsAcceptance: true }); // Stop the promise chain
            }

            // User đã đồng ý terms rồi - set login states và continue
            console.log("✅ User đã đồng ý terms - tiếp tục flow bình thường");
            setIsLoggedIn(true);
            setUserID(user.uid);
            localStorage.setItem("userId", user.uid);
            setIsCheckingNewUser(false); // Done checking

            // Thêm tracking affiliate
            try {
              console.log("🔄 Bắt đầu ghi nhận thông tin affiliate...");
              return trackAffiliateSignup(user.uid); // Theo dõi việc đăng ký qua tiếp thị liên kết
            } catch (affiliateError) {
              console.error(
                "❌ Lỗi khi ghi nhận thông tin affiliate:",
                affiliateError
              );
              return Promise.resolve(); // Tiếp tục dù có lỗi
            }
          })
          .then(() => {
            console.log("✅ Hoàn thành ghi nhận thông tin affiliate");

            // Track ad conversion if we have fbclid
            if (adParams.fbclid) {
              console.log("📊 Tracking ad conversion for registration");
              // Track a custom event for ad conversion
              trackEvent("AdConversion", {
                content_name: "registration",
                user_id: user.uid,
                ...adParams,
              });
            }

            // Chuyển hướng người dùng sau khi xử lý thành công
            // Using window.location.href for direct navigation, which is more reliable than React Router in this context
            console.log(
              "🔄 Chuyển hướng người dùng sau khi lưu dữ liệu thành công"
            );
            localStorage.setItem("redirecting", "true");
            const redirectUrl =
              localStorage.getItem("returnUrl") || "/ai-price";
            window.location.href = redirectUrl;
          })
          .catch((error) => {
            // Check if this is a "controlled" rejection for terms acceptance
            if (error && error.needsTermsAcceptance) {
              console.log("⏸️ Dừng lại để hiển thị modal terms (user cần đồng ý terms)");
              return; // Don't navigate, just show the modal
            }

            console.error("❌ Lỗi trong quá trình xử lý sau đăng nhập:", error);

            // Reset checking flag
            setIsCheckingNewUser(false);

            // Vẫn chuyển hướng người dùng dù có lỗi
            localStorage.setItem("redirecting", "true");
            const redirectUrl =
              localStorage.getItem("returnUrl") || "/ai-price";
            window.location.href = redirectUrl;
          });
      })
      .catch((error) => {
        // Đánh dấu popup Google đã đóng
        setIsGooglePopupOpen(false);
        // Reset checking flag
        setIsCheckingNewUser(false);

        console.error("Lỗi đăng nhập Google:", error);

        // Nếu lỗi là do người dùng đóng popup
        if (error.code === "auth/popup-closed-by-user") {
          alert("Quá trình đăng nhập bị hủy. Vui lòng thử lại.");
        } else {
          alert("Đăng nhập Google không thành công!");
        }

        setIsLoading(false);
        setIsManualLogin(false);
      });
  };

  return (
    <>
      <div className="login-container">
        <div className="login-content">
          <div className="login-header">
            <img
              src={logo}
              alt="AIPrice"
              className="login-logo mx-auto w-48 md:w-96"
            />
          </div>
          <div className="login-box">
            <h1 className="login-title">Chào mừng bạn đến với AI Price</h1>
            <p className="login-subtitle">Kết nối để tiếp tục sử dụng dịch vụ</p>
            <>
              <button
                className="login-button google-login"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <FaGoogle className="google-icon" />
                <span>
                  {isLoading ? "Đang kết nối..." : "Tiếp tục với Google"}
                </span>
              </button>
              <div className="terms">
                Bằng cách tiếp tục, bạn đồng ý với{" "}
                <a href="/terms">Điều khoản dịch vụ</a> và{" "}
                <a href="/privacy">Chính sách bảo mật</a> của chúng tôi
              </div>
            </>
          </div>
        </div>
      </div>

      {/* NEW: Terms Acceptance Modal for first-time users */}
      {showTermsModal && newUserData && (
        <TermsAcceptanceModal
          onAccept={handleTermsAccept}
          onDecline={handleTermsDecline}
          userName={newUserData.displayName}
        />
      )}
    </>
  );
};

export default LoginGoogle;
